import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { personalNotesApi } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { toast } from 'sonner';
import {
  NotebookPen, Plus, Pencil, Trash2, Paperclip, AlertTriangle,
  ArrowUpDown, CheckCircle2, Circle,
} from 'lucide-react';
import PersonalTaskDialog from './PersonalTaskDialog';

const STATUS_LABELS = { da_fare: 'Da fare', in_corso: 'In corso', completata: 'Completata' };
const STATUS_COLORS = {
  da_fare: 'text-slate-600 bg-slate-100',
  in_corso: 'text-amber-700 bg-amber-100',
  completata: 'text-emerald-700 bg-emerald-100',
};
const NOTE_SAVE_DELAY_MS = 900;

function isOverdue(task) {
  if (!task.due_date || task.status === 'completata') return false;
  const today = new Date().toISOString().slice(0, 10);
  return task.due_date < today;
}

// Icona globale "Note personali" — disponibile in qualsiasi pagina, riservata
// ad Area Manager e Operations Manager (richiesta utente 2026-08). Contiene
// un blocco note libero con autosave + una task list privata con scadenze,
// stato e allegati.
export default function PersonalWorkspaceButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortDesc, setSortDesc] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const noteTimer = useRef(null);
  const noteLoaded = useRef(false);

  const hidden = !user || !['area_manager', 'operations_manager'].includes(user.role);

  const refreshTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { data } = await personalNotesApi.listTasks(params);
      setTasks(data || []);
    } catch (e) {
      toast.error('Errore caricamento task personali');
    } finally { setLoadingTasks(false); }
  }, [statusFilter]);

  useEffect(() => {
    if (!open || hidden) return;
    noteLoaded.current = false;
    (async () => {
      try {
        const { data } = await personalNotesApi.getNote();
        setNoteText(data?.text || '');
      } catch (e) {
        // silent: blocco note non è critico per il resto dell'app
      } finally { noteLoaded.current = true; }
    })();
    refreshTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (open) refreshTasks(); }, [statusFilter, open, refreshTasks]);

  // Autosave del blocco note: debounce 900ms dopo l'ultima digitazione.
  useEffect(() => {
    if (!open || !noteLoaded.current) return;
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      setSavingNote(true);
      try {
        await personalNotesApi.saveNote(noteText);
      } catch (e) {
        toast.error('Errore salvataggio nota');
      } finally { setSavingNote(false); }
    }, NOTE_SAVE_DELAY_MS);
    return () => clearTimeout(noteTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteText]);

  async function toggleQuickComplete(task) {
    const newStatus = task.status === 'completata' ? 'da_fare' : 'completata';
    try {
      await personalNotesApi.updateTask(task.task_id, { status: newStatus });
      setTasks((prev) => prev.map((t) => (t.task_id === task.task_id ? { ...t, status: newStatus } : t)));
    } catch (e) {
      toast.error('Errore aggiornamento stato');
    }
  }

  async function handleDeleteTask(task) {
    if (!window.confirm(`Eliminare la task "${task.title}"?`)) return;
    try {
      await personalNotesApi.deleteTask(task.task_id);
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      toast.success('Task eliminata');
    } catch (e) {
      toast.error('Errore eliminazione task');
    }
  }

  if (hidden) return null;

  const sortedTasks = [...tasks].sort((a, b) => sortDesc
    ? b.created_at.localeCompare(a.created_at)
    : a.created_at.localeCompare(b.created_at));

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 hover:bg-slate-100 transition-colors"
        aria-label="Note personali"
        data-testid="personal-workspace-btn"
      >
        <NotebookPen className="w-5 h-5 text-slate-700" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="personal-workspace-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><NotebookPen className="w-4 h-4" /> Le mie note</SheetTitle>
          </SheetHeader>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">Blocco note</span>
              <span className="text-[10px] text-slate-400">{savingNote ? 'Salvataggio…' : 'Salvato'}</span>
            </div>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              placeholder="Scrivi qui i tuoi appunti personali…"
              data-testid="personal-note-textarea"
            />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide font-semibold text-slate-500">Le mie task</span>
              <Button size="sm" variant="outline" className="h-7 gap-1"
                onClick={() => { setEditingTask(null); setShowTaskDialog(true); }}
                data-testid="personal-task-new-btn">
                <Plus className="w-3.5 h-3.5" /> Nuova
              </Button>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="personal-task-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="da_fare">Da fare</SelectItem>
                  <SelectItem value="in_corso">In corso</SelectItem>
                  <SelectItem value="completata">Completata</SelectItem>
                </SelectContent>
              </Select>
              <button
                onClick={() => setSortDesc((s) => !s)}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 h-8 px-2 flex-shrink-0"
                data-testid="personal-task-sort-toggle"
                title="Ordina per data di inserimento"
              >
                <ArrowUpDown className="w-3.5 h-3.5" /> {sortDesc ? 'Più recenti' : 'Più vecchie'}
              </button>
            </div>

            {loadingTasks && <p className="text-xs text-slate-400 text-center py-6">Caricamento…</p>}
            {!loadingTasks && sortedTasks.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-6" data-testid="personal-task-empty">
                Nessuna task. Crea la prima!
              </p>
            )}
            <div className="space-y-2">
              {sortedTasks.map((task) => {
                const overdue = isOverdue(task);
                return (
                  <div key={task.task_id}
                    className={`border px-3 py-2.5 ${overdue ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    data-testid={`personal-task-item-${task.task_id}`}>
                    <div className="flex items-start gap-2">
                      <button onClick={() => toggleQuickComplete(task)} className="mt-0.5 flex-shrink-0"
                        data-testid={`personal-task-toggle-${task.task_id}`}
                        aria-label={task.status === 'completata' ? 'Segna come da fare' : 'Segna come completata'}>
                        {task.status === 'completata'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <Circle className="w-4 h-4 text-slate-400" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${task.status === 'completata' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 ${STATUS_COLORS[task.status]}`}>
                            {STATUS_LABELS[task.status]}
                          </span>
                          {task.due_date && (
                            <span className={`text-[11px] flex items-center gap-1 ${overdue ? 'text-red-700 font-semibold' : 'text-slate-500'}`}
                              data-testid={`personal-task-due-${task.task_id}`}>
                              {overdue && <AlertTriangle className="w-3 h-3" />}
                              Scadenza {new Date(task.due_date).toLocaleDateString('it-IT')}
                            </span>
                          )}
                          {task.attachments?.length > 0 && (
                            <span className="text-[11px] text-slate-400 flex items-center gap-0.5">
                              <Paperclip className="w-3 h-3" /> {task.attachments.length}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => { setEditingTask(task); setShowTaskDialog(true); }}
                          data-testid={`personal-task-edit-${task.task_id}`}>
                          <Pencil className="w-3.5 h-3.5 text-slate-400 hover:text-slate-900" />
                        </button>
                        <button onClick={() => handleDeleteTask(task)}
                          data-testid={`personal-task-delete-${task.task_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showTaskDialog && (
        <PersonalTaskDialog
          task={editingTask}
          onClose={() => setShowTaskDialog(false)}
          onSaved={() => { setShowTaskDialog(false); refreshTasks(); }}
        />
      )}
    </>
  );
}
